import { useQuery } from '@tanstack/react-query';

import { type FareFamilyPricingParams, fetchCharterFareFamilyPricing } from '~/services/CharterService';

/** Query keys for charter fare-family pricing — keyed by full param set so cache hits exact match. */
export const charterFareFamilyPricingQueryKeys = {
  all: ['charter-fare-family-pricing'] as const,
  list: (params: FareFamilyPricingParams) => [...charterFareFamilyPricingQueryKeys.all, params] as const,
};

/**
 * Fetches charter fare-family pricing for the given route.
 *
 * Pass `null` to disable the query (e.g. before search is triggered).
 *
 * Using TanStack Query here gives us:
 *  - Automatic deduplication / cancellation via `signal` (no race on rapid
 *    Search clicks — late-arriving responses don't overwrite the latest).
 *  - Built-in `isFetching` / `error` states.
 *  - Caching: re-selecting the same route avoids a network call.
 */
export function useGetCharterFareFamilyPricing(params: FareFamilyPricingParams | null) {
  return useQuery({
    queryKey: params
      ? charterFareFamilyPricingQueryKeys.list(params)
      : [...charterFareFamilyPricingQueryKeys.all, 'disabled'],
    queryFn: async ({ signal }) => {
      if (!params) {
        return [];
      }
      const response = await fetchCharterFareFamilyPricing(params, { signal });
      return response.result;
    },
    enabled: Boolean(params),
  });
}
